<template>
  <transition name="rm-fade">
    <div v-if="open" class="rm-overlay" @click.self="close">
      <div class="rm-modal" role="dialog" aria-modal="true" aria-labelledby="rm-title">
        <header class="rm-header">
          <div class="rm-header-info">
            <span class="rm-eyebrow">RELATÓRIOS</span>
            <h3 class="rm-title" id="rm-title">{{ headerTitle }}</h3>
          </div>
          <button type="button" class="rm-close" @click="close" aria-label="Fechar">
            <i class="bx bx-x"></i>
          </button>
        </header>

        <!-- Stepper visual -->
        <div class="rm-stepper">
          <div class="rm-step" :class="{ 'rm-step-active': step === 1, 'rm-step-done': step > 1 }">
            <span class="rm-step-num">1</span> Modelo
          </div>
          <div class="rm-step-divider"></div>
          <div class="rm-step" :class="{ 'rm-step-active': step === 2, 'rm-step-done': step > 2 }">
            <span class="rm-step-num">2</span> Filtros
          </div>
          <div class="rm-step-divider"></div>
          <div class="rm-step" :class="{ 'rm-step-active': step === 3 }">
            <span class="rm-step-num">3</span> Pré-visualizar &amp; Exportar
          </div>
        </div>

        <div class="rm-body">
          <!-- STEP 1: escolha do template (ou favorito salvo) -->
          <div v-if="step === 1" class="rm-step-content">
            <p class="rm-helper">Escolha um modelo de relatório pronto. Você pode ajustar os filtros no próximo passo.</p>

            <!-- Favoritos do usuário -->
            <div v-if="savedReports.length > 0" class="rm-saved-block">
              <div class="rm-block-title"><i class="bx bx-star"></i> Meus relatórios salvos</div>
              <div class="rm-saved-list">
                <button
                  v-for="s in savedReports"
                  :key="s.id"
                  type="button"
                  class="rm-saved-item"
                  @click="loadSaved(s)"
                >
                  <span class="rm-saved-name">{{ s.name }}</span>
                  <span class="rm-saved-meta">{{ templateNameByKey(s.template_key) }}</span>
                  <i class="bx bx-x rm-saved-del" @click.stop="deleteSaved(s)"></i>
                </button>
              </div>
            </div>

            <div v-if="savedReports.length > 0" class="rm-block-title rm-mt"><i class="bx bx-collection"></i> Modelos</div>

            <div v-if="loadingTemplates" class="rm-loading">Carregando modelos…</div>

            <div v-else-if="templates.length === 0" class="rm-empty">
              Nenhum modelo de relatório disponível.
            </div>

            <div v-else class="rm-template-grid">
              <button
                v-for="t in templates"
                :key="t.key"
                type="button"
                class="rm-template-card"
                :class="{ 'rm-template-card-selected': selectedTemplate && selectedTemplate.key === t.key }"
                @click="selectTemplate(t)"
              >
                <div class="rm-template-icon">
                  <i :class="['bx', t.icon || 'bx-file']"></i>
                </div>
                <div class="rm-template-name">{{ t.name }}</div>
                <div class="rm-template-desc">{{ t.description }}</div>
              </button>

              <button
                type="button"
                class="rm-template-card rm-template-card-custom"
                :class="{ 'rm-template-card-selected': isCustom }"
                @click="selectCustom"
              >
                <div class="rm-template-icon"><i class="bx bx-edit-alt"></i></div>
                <div class="rm-template-name">Em branco</div>
                <div class="rm-template-desc">Montar do zero — escolha as dimensões e medidas que quiser.</div>
              </button>
            </div>
          </div>

          <!-- STEP 2: filtros (modo template) ou builder (modo custom) -->
          <div v-if="step === 2" class="rm-step-content">
            <p class="rm-helper" v-if="!isCustom">
              Os filtros já estão pré-preenchidos com o que está no dashboard. Ajuste se quiser.
            </p>
            <p class="rm-helper" v-else>
              Escolha as dimensões (linhas/colunas) e as medidas que deseja ver. Período e tags do dashboard já estão aplicados.
            </p>

            <div class="rm-filters">
              <div class="rm-field">
                <label class="rm-label">Período (de)</label>
                <input type="date" class="rm-input" v-model="localFilters.from" />
              </div>
              <div class="rm-field">
                <label class="rm-label">Período (até)</label>
                <input type="date" class="rm-input" v-model="localFilters.to" />
              </div>
              <div class="rm-field rm-field-full">
                <label class="rm-label">Estandes (opcional)</label>
                <div class="rm-tag-list">
                  <label
                    v-for="tag in availableTags"
                    :key="tag.id"
                    class="rm-tag-chip"
                    :class="{ 'rm-tag-chip-on': isTagSelected(tag.id) }"
                  >
                    <input
                      type="checkbox"
                      :checked="isTagSelected(tag.id)"
                      @change="toggleTag(tag.id)"
                    />
                    {{ tag.name }}
                  </label>
                  <span v-if="!availableTags.length" class="rm-empty-inline">— sem estandes —</span>
                </div>
              </div>
            </div>

            <!-- Builder livre — só aparece se template = custom -->
            <div v-if="isCustom" class="rm-builder">
              <div v-if="loadingSchema" class="rm-loading">Carregando schema…</div>
              <template v-else>
                <div class="rm-builder-grid">
                  <!-- Painel Linhas: dimensões agrupadas por categoria -->
                  <div class="rm-builder-col">
                    <div class="rm-builder-title">
                      <i class="bx bx-list-ul"></i> Linhas (agrupar por)
                      <input
                        v-if="schema.dimensions.length > 8"
                        type="text"
                        class="rm-builder-search"
                        v-model="dimSearch"
                        placeholder="Buscar dimensão…"
                      />
                    </div>
                    <div class="rm-builder-list rm-builder-list-grouped">
                      <div
                        v-for="group in groupedDimensions"
                        :key="group.key"
                        class="rm-builder-group"
                      >
                        <div class="rm-builder-group-title">{{ group.label }}</div>
                        <div class="rm-builder-group-chips">
                          <label
                            v-for="d in group.items"
                            :key="'r-' + d.key"
                            class="rm-builder-chip"
                            :class="{ 'rm-builder-chip-on': customSpec.rows.includes(d.key) }"
                          >
                            <input
                              type="checkbox"
                              :checked="customSpec.rows.includes(d.key)"
                              @change="toggleArr(customSpec.rows, d.key)"
                            />
                            {{ d.label }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Painel Medidas -->
                  <div class="rm-builder-col">
                    <div class="rm-builder-title"><i class="bx bx-bar-chart-square"></i> Medidas</div>
                    <div class="rm-builder-list">
                      <label
                        v-for="m in schema.measures"
                        :key="'m-' + m.key"
                        class="rm-builder-chip"
                        :class="{ 'rm-builder-chip-on': customSpec.measures.includes(m.key) }"
                      >
                        <input
                          type="checkbox"
                          :checked="customSpec.measures.includes(m.key)"
                          @change="toggleArr(customSpec.measures, m.key)"
                        />
                        {{ m.label }}
                      </label>
                    </div>
                  </div>
                </div>

                <div v-if="customWarning" class="rm-builder-warn">
                  <i class="bx bx-info-circle"></i> {{ customWarning }}
                </div>
              </template>
            </div>

            <!-- Filtros avançados (mesmo builder do /email/campaign) -->
            <div class="rm-advanced-filters">
              <div class="rm-block-title rm-mt"><i class="bx bx-filter-alt"></i> Filtros do participante</div>
              <ReportFilterBuilder
                :value="participantRules"
                :catalog="filterCatalog"
                :loading="loadingFilterCatalog"
                @input="onParticipantRulesChange"
              />
            </div>
          </div>

          <!-- STEP 3: preview + export -->
          <div v-if="step === 3" class="rm-step-content">
            <!-- Tabs (resumo/leituras) -->
            <div v-if="preview && !previewError" class="rm-tabs">
              <button
                type="button"
                class="rm-tab"
                :class="{ 'rm-tab-active': activeTab === 'summary' }"
                @click="activeTab = 'summary'"
              >
                <i class="bx bx-pie-chart-alt-2"></i> Resumo
              </button>
              <button
                v-if="!isCustom"
                type="button"
                class="rm-tab"
                :class="{ 'rm-tab-active': activeTab === 'details' }"
                @click="switchToDetails"
              >
                <i class="bx bx-list-ul"></i> Leituras
                <span v-if="details && details.rows" class="rm-tab-badge">{{ details.rows.length }}</span>
              </button>
            </div>

            <div v-if="loadingPreview" class="rm-loading">Calculando relatório…</div>

            <div v-else-if="previewError" class="rm-error">
              <i class="bx bx-error-circle"></i> {{ previewError }}
            </div>

            <!-- Tab Leituras -->
            <div v-else-if="preview && activeTab === 'details'">
              <div v-if="loadingDetails" class="rm-loading">Carregando leituras…</div>
              <div v-else-if="detailsError" class="rm-error"><i class="bx bx-error-circle"></i> {{ detailsError }}</div>
              <div v-else-if="!details || !details.rows.length" class="rm-empty">
                Sem leituras no período. <a href="#/settings/reports" target="_blank">Configurar colunas dos participantes</a>.
              </div>
              <div v-else class="rm-table-wrap">
                <div class="rm-table-title">
                  Leituras detalhadas
                  <span class="rm-table-hint">— colunas do participante vêm de <a href="#/settings/reports" target="_blank">/settings/reports</a></span>
                </div>
                <table class="rm-table">
                  <thead>
                    <tr>
                      <th v-for="(col, alias) in details.columns" :key="alias">{{ col.label }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in details.rows.slice(0, 5)" :key="i">
                      <td v-for="(col, alias) in details.columns" :key="alias">{{ row[alias] }}</td>
                    </tr>
                  </tbody>
                </table>
                <div class="rm-table-meta">
                  Mostrando 5 de {{ details.rows.length }} linhas. Exporte em Excel pra ver todas.
                </div>
              </div>
            </div>

            <!-- Tab Resumo (default) -->
            <div v-else-if="preview && activeTab === 'summary'">
              <!-- KPIs -->
              <div v-if="preview.sections && preview.sections.kpis" class="rm-kpis">
                <div class="rm-kpi">
                  <div class="rm-kpi-label">Entradas</div>
                  <div class="rm-kpi-value">{{ preview.sections.kpis.ins | num }}</div>
                </div>
                <div class="rm-kpi">
                  <div class="rm-kpi-label">Únicos</div>
                  <div class="rm-kpi-value">{{ preview.sections.kpis.unique_attendees | num }}</div>
                </div>
                <div class="rm-kpi">
                  <div class="rm-kpi-label">Taxa</div>
                  <div class="rm-kpi-value">
                    {{ preview.sections.kpis.attendance_rate !== null ? preview.sections.kpis.attendance_rate + '%' : '—' }}
                  </div>
                </div>
                <div class="rm-kpi">
                  <div class="rm-kpi-label">Negações</div>
                  <div class="rm-kpi-value">{{ preview.sections.kpis.denied_attempts | num }}</div>
                </div>
              </div>

              <!-- Tabela: primeiras 10 linhas — click numa linha = drill-down -->
              <div v-if="preview.rows && preview.rows.length" class="rm-table-wrap">
                <div class="rm-table-title">
                  Detalhamento (primeiras 10 linhas) <span class="rm-table-hint">— clique numa linha para detalhes</span>
                </div>
                <table class="rm-table">
                  <thead>
                    <tr>
                      <th v-for="col in preview.columns" :key="col.alias">{{ col.label }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, i) in preview.rows.slice(0, 5)"
                      :key="i"
                      class="rm-table-row-click"
                      @click="openDrill(row)"
                    >
                      <td v-for="col in preview.columns" :key="col.alias">{{ row[col.alias] }}</td>
                    </tr>
                  </tbody>
                </table>
                <div class="rm-table-meta">
                  {{ preview.rows.length }} linhas · gerado em {{ preview.meta.execution_ms }}ms<span v-if="preview.meta.cache_hit"> · cache</span>
                </div>
              </div>

              <div v-else class="rm-empty">Sem dados no período selecionado.</div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <footer class="rm-footer">
          <button type="button" class="rm-btn rm-btn-ghost" @click="close">Cancelar</button>

          <div class="rm-footer-spacer"></div>

          <button v-if="step > 1" type="button" class="rm-btn rm-btn-ghost" @click="back">
            <i class="bx bx-chevron-left"></i> Voltar
          </button>

          <button
            v-if="step === 3 && !!preview && !previewError && !isCustom"
            type="button"
            class="rm-btn rm-btn-ghost"
            @click="promptSave"
          >
            <i class="bx bx-star"></i> Salvar
          </button>

          <button
            v-if="step === 1"
            type="button"
            class="rm-btn rm-btn-primary"
            :disabled="!selectedTemplate"
            @click="goToStep(2)"
          >
            Avançar <i class="bx bx-chevron-right"></i>
          </button>

          <button
            v-if="step === 2"
            type="button"
            class="rm-btn rm-btn-primary"
            :disabled="isCustom && !!customWarning"
            @click="goToStep(3)"
          >
            Pré-visualizar <i class="bx bx-chevron-right"></i>
          </button>

          <button
            v-if="step === 3"
            type="button"
            class="rm-btn rm-btn-ghost"
            :disabled="loadingPreview || exporting || !!previewError"
            @click="exportFile('csv')"
          >
            <i class="bx bx-file"></i> CSV
          </button>
          <button
            v-if="step === 3"
            type="button"
            class="rm-btn rm-btn-ghost"
            :disabled="loadingPreview || exporting || !!previewError"
            @click="exportFile('pdf')"
          >
            <i class="bx bx-file-blank"></i> PDF
          </button>
          <button
            v-if="step === 3"
            type="button"
            class="rm-btn rm-btn-primary"
            :disabled="loadingPreview || exporting || !!previewError"
            @click="exportFile('xlsx')"
          >
            <i class="bx bx-download"></i>
            {{ exporting ? 'Gerando…' : 'Excel' }}
          </button>
        </footer>
      </div>

      <DrillDialog
        :open="drillOpen"
        :title="drillTitle"
        :row-filters="drillRowFilters"
        :base-filters="drillBaseFilters"
        @close="drillOpen = false"
      />
    </div>
  </transition>
</template>

<script>
import services from '../services'
import DrillDialog from './DrillDialog.vue'
import ReportFilterBuilder from './ReportFilterBuilder.vue'

const CUSTOM_TEMPLATE = {
  key: 'custom',
  name: 'Em branco',
  description: 'Personalizado',
  icon: 'bx-edit-alt'
}

export default {
  name: 'reports-modal',
  components: { DrillDialog, ReportFilterBuilder },
  filters: {
    num (v) {
      if (v === null || v === undefined || v === '') return '—'
      return Number(v).toLocaleString('pt-BR')
    }
  },
  props: {
    open: { type: Boolean, default: false },
    initialFilters: { type: Object, default: () => ({}) },
    availableTags: { type: Array, default: () => [] }
  },
  data: () => ({
    step: 1,
    templates: [],
    savedReports: [],
    loadingTemplates: false,
    selectedTemplate: null,
    localFilters: { from: '', to: '', tag_ids: [] },
    preview: null,
    loadingPreview: false,
    previewError: null,
    exporting: false,
    // Builder livre
    schema: { dimensions: [], measures: [], limits: {} },
    loadingSchema: false,
    customSpec: { rows: [], measures: [] },
    // Sub-modal de drill-down
    drillOpen: false,
    drillTitle: '',
    drillRowFilters: {},
    drillBaseFilters: {},
    // Aba "Leituras" (lazy)
    activeTab: 'summary',
    details: null,
    loadingDetails: false,
    detailsError: null,
    // Filtros avançados (builder de rules)
    participantRules: [],
    filterCatalog: [],
    loadingFilterCatalog: false,
    // Builder livre — busca de dimensões
    dimSearch: ''
  }),
  computed: {
    headerTitle () {
      if (this.step === 1) return 'Exportar Relatório de Check-in'
      if (this.selectedTemplate) return this.selectedTemplate.name
      return 'Exportar Relatório de Check-in'
    },
    isCustom () {
      return !!(this.selectedTemplate && this.selectedTemplate.key === 'custom')
    },
    customWarning () {
      if (!this.isCustom) return null
      if (this.customSpec.measures.length === 0) return 'Escolha ao menos uma medida.'
      const limits = this.schema.limits || {}
      if (limits.max_dimensions && this.customSpec.rows.length > limits.max_dimensions) {
        return `Máximo de ${limits.max_dimensions} dimensões. Reduza a seleção.`
      }
      if (limits.max_measures && this.customSpec.measures.length > limits.max_measures) {
        return `Máximo de ${limits.max_measures} medidas. Reduza a seleção.`
      }
      return null
    },
    /**
     * Dimensões agrupadas por categoria (Tempo, Estande, Operador,
     * Participante, Form Fields, etc.) — alimenta a UI do Builder livre.
     * Aplica filtro `dimSearch` por label.
     */
    groupedDimensions () {
      const labels = {
        time: 'Tempo',
        tag: 'Estande / Local',
        operator: 'Operador',
        event: 'Evento',
        participant: 'Participante',
        form_field: 'Campos do formulário',
        other: 'Outros'
      }
      const order = ['time', 'tag', 'operator', 'event', 'participant', 'form_field', 'other']
      const search = (this.dimSearch || '').trim().toLowerCase()

      const buckets = {}
      for (const d of this.schema.dimensions || []) {
        if (search && !d.label.toLowerCase().includes(search)) continue
        const cat = d.category || 'other'
        if (!buckets[cat]) buckets[cat] = []
        buckets[cat].push(d)
      }

      return order
        .filter((cat) => buckets[cat] && buckets[cat].length > 0)
        .map((cat) => ({ key: cat, label: labels[cat] || cat, items: buckets[cat] }))
    }
  },
  watch: {
    open (val) {
      if (val) this.handleOpen()
    }
  },
  methods: {
    handleOpen () {
      this.step = 1
      this.preview = null
      this.previewError = null
      this.activeTab = 'summary'
      this.details = null
      this.detailsError = null
      this.participantRules = []
      this.localFilters = this.normalizeInitialFilters(this.initialFilters)
      this.fetchTemplates().then(() => {
        if (this.templates.length === 1) {
          this.selectedTemplate = this.templates[0]
        }
      })
      this.fetchSaved()
      this.fetchFilterCatalog()
    },
    normalizeInitialFilters (f) {
      const toDate = (d) => {
        if (!d) return ''
        const date = d instanceof Date ? d : new Date(d)
        if (isNaN(date.getTime())) return ''
        const yyyy = date.getFullYear()
        const mm = String(date.getMonth() + 1).padStart(2, '0')
        const dd = String(date.getDate()).padStart(2, '0')
        return `${yyyy}-${mm}-${dd}`
      }
      return {
        from: toDate(f.from) || toDate(new Date()),
        to: toDate(f.to) || toDate(new Date()),
        tag_ids: Array.isArray(f.tagIds) ? [...f.tagIds] : []
      }
    },
    fetchTemplates () {
      this.loadingTemplates = true
      return services.reportTemplates()
        .then((res) => { this.templates = res.data || []; this.loadingTemplates = false })
        .catch(() => { this.templates = []; this.loadingTemplates = false })
    },
    fetchSaved () {
      services.reportSavedList()
        .then((res) => { this.savedReports = res.data || [] })
        .catch(() => { this.savedReports = [] })
    },
    fetchFilterCatalog () {
      if (this.filterCatalog.length > 0) return
      this.loadingFilterCatalog = true
      services.reportFilterCatalog()
        .then((res) => { this.filterCatalog = res.data || []; this.loadingFilterCatalog = false })
        .catch(() => { this.filterCatalog = []; this.loadingFilterCatalog = false })
    },
    onParticipantRulesChange (rules) {
      this.participantRules = Array.isArray(rules) ? rules : []
    },
    templateNameByKey (key) {
      const t = this.templates.find((x) => x.key === key)
      return t ? t.name : key
    },
    loadSaved (saved) {
      const tpl = this.templates.find((t) => t.key === saved.template_key)
      if (!tpl) return
      this.selectedTemplate = tpl
      const f = saved.filters || {}
      this.localFilters = {
        from: f.from || this.localFilters.from,
        to: f.to || this.localFilters.to,
        tag_ids: Array.isArray(f.tag_ids) ? [...f.tag_ids] : []
      }
      this.goToStep(3)
    },
    deleteSaved (saved) {
      if (!window.confirm(`Excluir "${saved.name}"?`)) return
      services.reportSavedDestroy(saved.id).then(() => {
        this.savedReports = this.savedReports.filter((s) => s.id !== saved.id)
      })
    },
    promptSave () {
      if (!this.selectedTemplate) return
      const name = window.prompt('Salvar este relatório como:', this.selectedTemplate.name)
      if (!name || !name.trim()) return
      services.reportSavedStore({
        name: name.trim(),
        template_key: this.selectedTemplate.key,
        filters: this.buildFiltersPayload()
      }).then(() => { this.fetchSaved() })
    },
    selectTemplate (t) {
      this.selectedTemplate = t
    },
    selectCustom () {
      this.selectedTemplate = { ...CUSTOM_TEMPLATE }
      // Defaults razoáveis pra primeira spec
      if (this.customSpec.rows.length === 0 && this.customSpec.measures.length === 0) {
        this.customSpec = { rows: ['time.date'], measures: ['ins', 'unique_attendees'] }
      }
      if (this.schema.dimensions.length === 0) this.fetchSchema()
    },
    fetchSchema () {
      this.loadingSchema = true
      services.reportSchema()
        .then((res) => { this.schema = res.data || { dimensions: [], measures: [], limits: {} }; this.loadingSchema = false })
        .catch(() => { this.schema = { dimensions: [], measures: [], limits: {} }; this.loadingSchema = false })
    },
    toggleArr (arr, value) {
      const idx = arr.indexOf(value)
      if (idx >= 0) arr.splice(idx, 1)
      else arr.push(value)
    },
    isTagSelected (id) {
      return this.localFilters.tag_ids.includes(id)
    },
    toggleTag (id) {
      const idx = this.localFilters.tag_ids.indexOf(id)
      if (idx >= 0) this.localFilters.tag_ids.splice(idx, 1)
      else this.localFilters.tag_ids.push(id)
    },
    goToStep (n) {
      this.step = n
      if (n === 3) this.loadPreview()
    },
    back () { if (this.step > 1) this.step-- },
    close () { this.$emit('close') },
    buildFiltersPayload () {
      return {
        from: this.localFilters.from,
        to: this.localFilters.to,
        tag_ids: this.localFilters.tag_ids,
        participant_rules: this.participantRules
      }
    },
    /**
     * Monta a ReportSpec completa pra modo Custom: rows/measures escolhidos +
     * filtros derivados (period, tags) iguais aos do template path.
     */
    buildCustomSpec () {
      const filters = []
      if (this.localFilters.from && this.localFilters.to) {
        filters.push({ field: 'time.date', op: 'between', value: [this.localFilters.from, this.localFilters.to] })
      }
      if (this.localFilters.tag_ids && this.localFilters.tag_ids.length) {
        filters.push({ field: 'tag.id', op: 'in', value: this.localFilters.tag_ids })
      }
      return {
        dataset: 'checkin_events',
        rows: [...this.customSpec.rows],
        measures: [...this.customSpec.measures],
        filters,
        participant_rules: this.participantRules,
        options: { sort: this.customSpec.measures[0] ? { by: this.customSpec.measures[0], dir: 'desc' } : null }
      }
    },
    loadPreview () {
      if (!this.selectedTemplate) return
      this.loadingPreview = true
      this.preview = null
      this.previewError = null
      const done = () => { this.loadingPreview = false }
      const promise = this.isCustom
        ? services.reportCustomPreview(this.buildCustomSpec())
        : services.reportPreview(this.selectedTemplate.key, this.buildFiltersPayload())
      promise
        .then((res) => { this.preview = res.data; done() })
        .catch((err) => {
          const msg = (err && err.response && err.response.data && err.response.data.message) ||
                      (err && err.message) ||
                      'Erro ao gerar pré-visualização.'
          this.previewError = msg
          done()
        })
    },
    /**
     * Drill-down: extrai o valor das dimensões da linha clicada e abre o sub-dialog.
     * Só funciona se a tabela tem dimensões (ou seja, agregada). Se não houver
     * dimensão alguma na linha, drill não faz sentido — abrimos mesmo assim
     * pra usuário ver leituras com os filtros base.
     */
    openDrill (row) {
      const rowFilters = {}
      if (this.preview && Array.isArray(this.preview.columns)) {
        for (const col of this.preview.columns) {
          if (col.kind === 'dimension') {
            const v = row[col.alias]
            if (v !== null && v !== undefined && v !== '') {
              rowFilters[col.key] = v
            }
          }
        }
      }
      this.drillTitle = (this.selectedTemplate ? this.selectedTemplate.name : 'Detalhe') + ' — leituras'
      this.drillRowFilters = rowFilters
      this.drillBaseFilters = this.buildFiltersPayload()
      this.drillOpen = true
    },
    /**
     * Ativa aba "Leituras" e carrega detailedRows lazy se ainda não carregou.
     * Usa .then/.catch separados pra evitar bug do .finally() no Vue 2 que
     * disparava "Cannot read properties of undefined (reading '_wrapper')".
     */
    switchToDetails () {
      this.activeTab = 'details'
      if (this.details || this.loadingDetails || this.detailsError) return
      if (!this.selectedTemplate || this.isCustom) return
      this.loadingDetails = true
      const done = () => { this.loadingDetails = false }
      services.reportDetails(this.selectedTemplate.key, this.buildFiltersPayload())
        .then((res) => { this.details = res.data; done() })
        .catch((err) => {
          const msg = (err && err.response && err.response.data && err.response.data.message) ||
                      (err && err.message) ||
                      'Erro ao carregar leituras.'
          this.detailsError = msg
          done()
        })
    },
    exportFile (format = 'xlsx') {
      if (!this.selectedTemplate) return
      this.exporting = true
      const promise = this.isCustom
        ? services.reportCustomExport(this.buildCustomSpec(), format)
        : services.reportExport(this.selectedTemplate.key, this.buildFiltersPayload(), format)
      const done = () => { this.exporting = false }
      promise
        .then((blob) => {
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `relatorio-${this.selectedTemplate.key}-${this.localFilters.from}-a-${this.localFilters.to}.${format}`
          document.body.appendChild(a)
          a.click()
          a.remove()
          window.URL.revokeObjectURL(url)
          done()
        })
        .catch((err) => {
          const msg = (err && err.response && err.response.data && err.response.data.message) ||
                      (err && err.message) ||
                      'Erro ao gerar arquivo.'
          this.previewError = msg
          done()
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.rm-overlay {
  position: fixed; inset: 0; z-index: 1100;
  background: rgba(20, 24, 38, 0.55);
  display: grid; place-items: center;
  padding: 24px;
}
.rm-modal {
  width: 100%; max-width: 760px; max-height: 90vh;
  background: #fff; border-radius: 12px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.25);
  display: flex; flex-direction: column;
  overflow: hidden;
}
.rm-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 20px 24px 12px;
  border-bottom: 1px solid #eaecf2;
}
.rm-header-info { display: flex; flex-direction: column; gap: 2px; }
.rm-eyebrow {
  font-size: 10px; font-weight: 700; letter-spacing: 1.5px;
  color: #8b90a5; text-transform: uppercase;
}
.rm-title { font-size: 18px; font-weight: 600; color: #1a1d26; margin: 0; }
.rm-close {
  background: transparent; border: 0; padding: 6px; cursor: pointer;
  font-size: 22px; color: #8b90a5; border-radius: 6px;
  &:hover { background: #f5f6fa; color: #1a1d26; }
}

.rm-stepper {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 24px; background: #f7f8fb;
  border-bottom: 1px solid #eaecf2;
  font-size: 12px; color: #8b90a5;
}
.rm-step {
  display: inline-flex; align-items: center; gap: 6px; font-weight: 500;
  &.rm-step-active { color: #556ee6; .rm-step-num { background: #556ee6; color: #fff; } }
  &.rm-step-done   { color: #34c38f; .rm-step-num { background: #34c38f; color: #fff; } }
}
.rm-step-num {
  width: 22px; height: 22px; border-radius: 50%; background: #eaecf2; color: #8b90a5;
  display: grid; place-items: center; font-size: 11px; font-weight: 700;
}
.rm-step-divider { flex: 1; height: 1px; background: #eaecf2; }

.rm-body {
  flex: 1; overflow-y: auto; padding: 20px 24px;
}
.rm-helper { font-size: 13px; color: #4a5068; margin: 0 0 16px; }

.rm-loading, .rm-empty, .rm-empty-inline { color: #8b90a5; font-size: 13px; padding: 16px 0; }
.rm-error {
  display: flex; align-items: center; gap: 8px;
  background: #fef2f2; color: #c62828; border: 1px solid #fecaca;
  border-radius: 8px; padding: 12px; font-size: 13px;
}

.rm-block-title {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; color: #4a5068; margin-bottom: 10px;
  text-transform: uppercase; letter-spacing: 0.5px;
  i { color: #f1b44c; font-size: 14px; }
}
.rm-mt { margin-top: 20px; }

.rm-saved-block { margin-bottom: 4px; }
.rm-saved-list { display: flex; flex-direction: column; gap: 6px; margin-bottom: 8px; }
.rm-saved-item {
  display: flex; align-items: center; gap: 10px;
  background: #fef9ef; border: 1px solid #fbe5b6;
  border-radius: 8px; padding: 9px 12px; cursor: pointer; text-align: left;
  transition: all 0.15s ease;
  &:hover { background: #fdf2dc; border-color: #f1b44c; }
}
.rm-saved-name { flex: 1; font-weight: 500; color: #1a1d26; font-size: 13px; }
.rm-saved-meta { color: #8b90a5; font-size: 11px; }
.rm-saved-del {
  color: #b0b5c8; padding: 2px; border-radius: 4px;
  &:hover { color: #c62828; background: #fee; }
}

.rm-template-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px;
}
.rm-template-card {
  text-align: left; background: #fff; border: 1px solid #eaecf2; border-radius: 10px;
  padding: 14px; cursor: pointer; transition: all 0.15s ease;
  display: flex; flex-direction: column; gap: 6px;
  &:hover { border-color: #c8cfeb; background: #f7f8fb; }
  &.rm-template-card-selected {
    border-color: #556ee6; background: #f0f3fe;
    box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.15);
  }
  &.rm-template-card-custom {
    border-style: dashed;
    .rm-template-icon { color: #f1b44c; }
  }
}
.rm-template-icon { font-size: 22px; color: #556ee6; }
.rm-template-name { font-weight: 600; font-size: 14px; color: #1a1d26; }
.rm-template-desc { font-size: 12px; color: #6b7088; line-height: 1.4; }

.rm-filters {
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
}
.rm-field { display: flex; flex-direction: column; gap: 6px; }
.rm-field-full { grid-column: 1 / -1; }
.rm-label { font-size: 12px; font-weight: 500; color: #4a5068; }
.rm-input {
  padding: 8px 10px; border: 1px solid #d8dbe4; border-radius: 6px;
  font-size: 13px; color: #1a1d26;
  &:focus { border-color: #556ee6; outline: 0; box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.15); }
}
.rm-tag-list { display: flex; flex-wrap: wrap; gap: 6px; }
.rm-tag-chip {
  display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px;
  background: #fff; border: 1px solid #d8dbe4; border-radius: 999px;
  font-size: 12px; color: #4a5068; cursor: pointer; transition: all 0.15s ease;
  input { display: none; }
  &:hover { border-color: #556ee6; }
  &.rm-tag-chip-on { background: #556ee6; border-color: #556ee6; color: #fff; }
}

.rm-kpis {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px;
}
.rm-kpi {
  background: #f7f8fb; border-radius: 10px; padding: 14px;
}
.rm-kpi-label { font-size: 11px; font-weight: 600; color: #8b90a5; text-transform: uppercase; letter-spacing: 0.5px; }
.rm-kpi-value { font-size: 22px; font-weight: 700; color: #1a1d26; margin-top: 4px; }

.rm-tabs {
  display: flex; gap: 4px; padding: 4px; background: #f7f8fb;
  border-radius: 8px; margin-bottom: 14px; width: fit-content;
}
.rm-tab {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px; background: transparent; border: 0;
  font-size: 12px; font-weight: 500; color: #4a5068; cursor: pointer;
  border-radius: 6px; transition: all 0.15s ease;
  &:hover { background: rgba(85, 110, 230, 0.08); }
  &.rm-tab-active { background: #fff; color: #556ee6; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
}
.rm-tab-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px;
  background: #556ee6; color: #fff; border-radius: 999px;
  font-size: 10px; font-weight: 700;
}

.rm-table-title { font-size: 12px; font-weight: 600; color: #4a5068; margin: 12px 0 8px; }
.rm-table-hint { font-weight: 400; color: #8b90a5; a { color: #556ee6; } }
.rm-table-wrap { overflow-x: auto; }
.rm-table {
  width: 100%; border-collapse: collapse; font-size: 12px;
  th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid #eaecf2; }
  th { background: #f7f8fb; color: #4a5068; font-weight: 600; }
  td { color: #1a1d26; }
}
.rm-table-row-click {
  cursor: pointer; transition: background 0.12s ease;
  &:hover td { background: #f0f3fe; }
}
.rm-table-meta { font-size: 11px; color: #8b90a5; margin-top: 8px; }

/* Builder livre */
.rm-builder { margin-top: 18px; padding-top: 14px; border-top: 1px dashed #d8dbe4; }
.rm-builder-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
}
.rm-builder-col { display: flex; flex-direction: column; gap: 8px; }
.rm-builder-title {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 600; color: #4a5068;
  text-transform: uppercase; letter-spacing: 0.5px;
  i { color: #556ee6; font-size: 14px; }
}
.rm-builder-list {
  display: flex; flex-wrap: wrap; gap: 6px;
  max-height: 240px; overflow-y: auto;
  padding: 6px; background: #f7f8fb; border-radius: 8px;
}
.rm-builder-list-grouped {
  flex-direction: column; gap: 8px;
}
.rm-builder-group { display: flex; flex-direction: column; gap: 4px; }
.rm-builder-group-title {
  font-size: 10px; font-weight: 700; letter-spacing: 0.8px;
  color: #8b90a5; text-transform: uppercase; padding: 0 2px;
}
.rm-builder-group-chips { display: flex; flex-wrap: wrap; gap: 4px; }
.rm-builder-search {
  margin-left: auto; padding: 4px 8px;
  border: 1px solid #d8dbe4; border-radius: 6px;
  font-size: 11px; font-weight: 400; width: 140px;
  text-transform: none; letter-spacing: normal;
  &:focus { border-color: #556ee6; outline: 0; }
}
.rm-builder-chip {
  display: inline-flex; align-items: center; padding: 5px 10px;
  background: #fff; border: 1px solid #d8dbe4; border-radius: 999px;
  font-size: 11px; color: #4a5068; cursor: pointer; transition: all 0.15s ease;
  input { display: none; }
  &:hover { border-color: #556ee6; }
  &.rm-builder-chip-on { background: #556ee6; border-color: #556ee6; color: #fff; }
}
.rm-builder-warn {
  margin-top: 12px; padding: 10px 12px; background: #fff8e1;
  border: 1px solid #fde68a; color: #92400e; border-radius: 8px;
  font-size: 12px; display: flex; align-items: center; gap: 6px;
  i { font-size: 16px; }
}

.rm-footer {
  display: flex; align-items: center; gap: 8px;
  padding: 16px 24px; border-top: 1px solid #eaecf2; background: #fafbfd;
}
.rm-footer-spacer { flex: 1; }
.rm-btn {
  display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px;
  border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer;
  border: 1px solid transparent; transition: all 0.15s ease;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
.rm-btn-primary {
  background: #556ee6; color: #fff;
  &:hover:not(:disabled) { background: #4458c9; }
}
.rm-btn-ghost {
  background: transparent; color: #4a5068; border-color: #d8dbe4;
  &:hover:not(:disabled) { background: #f5f6fa; }
}

.rm-fade-enter-active, .rm-fade-leave-active { transition: opacity 0.2s ease; }
.rm-fade-enter, .rm-fade-leave-to { opacity: 0; }

@media (max-width: 720px) {
  .rm-filters { grid-template-columns: 1fr; }
  .rm-kpis { grid-template-columns: repeat(2, 1fr); }
  .rm-stepper { font-size: 11px; }
}
</style>
